import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';

import { useToggle } from 'Shared/utils/hooks';
import { fetchWorkspaceTemplates } from 'Shared/actions/WorkspaceTemplates.actions';
import * as WorkspaceTemplatesSelectors from 'Shared/selectors/WorkspaceTemplates.selectors';
import WpTabs from 'Wpds/components/tabs/WpTabs';
import WpTab from 'Wpds/components/tabs/WpTab';
import { WorkspaceTemplateType } from 'Shared/constants/types';
import Spinner from 'Shared/components/Spinner';
import { useFlags } from 'launchdarkly-react-client-sdk';

import WorkspaceTemplateBlock from './WorkspaceTemplateBlock/WorkspaceTemplateBlock';
import BlankWorkspaceTemplateBlock from './BlankWorkspaceTemplateBlock/BlankWorkspaceTemplateBlock';
import WpButton from 'Wpds/components/button/WpButton';

import './TemplateGallery.scss';

// Hooks
import { useApp } from 'Shared/utils/hooks/use-app';

const recommendedTemplates = Object.freeze(['weekly_one_on_one_meeting', 'weekly_team_meeting']);

type Props = {
  isInitialChoice?: boolean;
  isGroup: boolean;
  useTwoColumns?: boolean;
  startWithRecommendedTemp?: boolean;
  onSubmit?: Function;
  ovPreviewTemplateClose?: Function;
  onPreviewTemplateBack?: Function;
  onCreateTemplate?: Function;
};

function TemplateGallery({
  isInitialChoice = false,
  isGroup,
  useTwoColumns = false,
  startWithRecommendedTemp = false,
  onSubmit,
  ovPreviewTemplateClose,
  onPreviewTemplateBack,
  onCreateTemplate,
}: Props) {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState(isGroup ? WorkspaceTemplateType.team : WorkspaceTemplateType.oneOnOne);
  const [isLoading, toggleIsLoading] = useToggle(false);
  const { customWorkspaceTemplates: customWorkspaceTemplatesFlag } = useFlags();
  const { isZoom } = useApp();

  const workspaceTemplates = useSelector(state =>
    WorkspaceTemplatesSelectors.getWorkspaceTemplatesByType(state, { type: activeTab })
  )
    .valueSeq()
    .sortBy(a => a.get('createdAtTimestamp'))
    .sort((a, b) => b.get('isCustom') - a.get('isCustom'))
    .sort((a, b) => b.get('piRecommended') - a.get('piRecommended'))
    .toJS();

  useEffect(() => {
    async function fetchData() {
      toggleIsLoading();
      await dispatch(fetchWorkspaceTemplates({ type: activeTab })).finally(() => {
        setTimeout(toggleIsLoading, 300);
      });
    }

    fetchData();

    if (!isInitialChoice) {
      (window as any).analytics.track('Viewed Workspace Template Gallery', { template_type: activeTab });
    }
  }, [activeTab]);

  return (
    <div className="TemplateGallery h-100 mb-4">
      {!isZoom && !startWithRecommendedTemp && (
        <div className="TemplateGallery__tabs-and-button-container">
          <WpTabs onTabClick={setActiveTab} activeTab={activeTab}>
            <WpTab tabId={WorkspaceTemplateType.oneOnOne}>1:1 Meetings</WpTab>
            <WpTab tabId={WorkspaceTemplateType.team}>Team Meetings</WpTab>
          </WpTabs>
          {onCreateTemplate && customWorkspaceTemplatesFlag && (
            <WpButton onClick={() => onCreateTemplate()}>Create new template</WpButton>
          )}
        </div>
      )}
      {isLoading ? (
        <Spinner activeIf className="my-5 h-100" />
      ) : (
        <div
          className={cn('TemplateGallery__templates mt-4', {
            'TemplateGallery__templates--two-columns': useTwoColumns,
          })}
        >
          {isInitialChoice && <BlankWorkspaceTemplateBlock />}
          {workspaceTemplates.map(template => (
            <WorkspaceTemplateBlock
              startWithPreview={startWithRecommendedTemp && recommendedTemplates.includes(template.slug)}
              key={template.slug}
              template={template}
              isInitialChoice={isInitialChoice}
              ovPreviewTemplateClose={ovPreviewTemplateClose}
              onPreviewTemplateBack={onPreviewTemplateBack}
              onSubmit={onSubmit}
              isGroupWorkspace={isGroup}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default TemplateGallery;
